import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  Bell,
  Boxes,
  Building2,
  ChartNoAxesColumn,
  Cog,
  Gauge,
  HardHat,
  Map,
  Moon,
  Radio,
  Search,
  ShieldAlert,
  Sparkles,
  Sun,
  UserRound,
  Users,
  Workflow,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS, type NocSummary, type PermissionKey } from '@oss/shared';
import { api, tokenStore } from '@/lib/api-client';
import { realtime } from '@/lib/realtime';
import { useAuth, usePermissions } from '@/lib/permissions';
import { ErrorBoundary } from '../ErrorBoundary';

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  permission: PermissionKey;
  /**
   * False for modules that have no route yet. They stay visible so the shape
   * of the system is legible, but render as disabled rather than as links
   * that bounce silently back to the dashboard — a nav item that appears to
   * do nothing when clicked is worse than one that says it is not ready.
   */
  built?: boolean;
}

const NAV: NavItem[] = [
  { to: '/noc', label: 'NOC', icon: Gauge, permission: PERMISSIONS.NOC_DASHBOARD_READ, built: true },
  { to: '/alarms', label: 'Alarms', icon: Bell, permission: PERMISSIONS.ALARM_READ, built: true },
  { to: '/incidents', label: 'Incidents', icon: Activity, permission: PERMISSIONS.INCIDENT_READ, built: true },
  { to: '/monitoring', label: 'Monitoring', icon: Radio, permission: PERMISSIONS.DEVICE_READ, built: true },
  { to: '/gis', label: 'GIS & Fiber', icon: Map, permission: PERMISSIONS.GIS_READ, built: true },
  { to: '/sites', label: 'Sites', icon: Building2, permission: PERMISSIONS.SITE_READ, built: true },
  { to: '/subscribers', label: 'Subscribers', icon: Users, permission: PERMISSIONS.SUBSCRIBER_READ, built: true },
  { to: '/field', label: 'Field Ops', icon: HardHat, permission: PERMISSIONS.JOB_READ, built: true },
  { to: '/assets', label: 'Assets', icon: Boxes, permission: PERMISSIONS.ASSET_READ },
  { to: '/analytics', label: 'Analytics', icon: ChartNoAxesColumn, permission: PERMISSIONS.ANALYTICS_READ, built: true },
  { to: '/automation', label: 'Automation', icon: Workflow, permission: PERMISSIONS.AUTOMATION_READ },
  { to: '/ai', label: 'AI Ops', icon: Sparkles, permission: PERMISSIONS.AI_USE },
  { to: '/admin', label: 'Admin', icon: Cog, permission: PERMISSIONS.USER_READ, built: true },
];

export function AppShell() {
  const { has } = usePermissions();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('oss.sidebar') === 'collapsed',
  );

  // The WebSocket gateway is not implemented yet. Connecting anyway produced
  // a permanent reconnect loop against an endpoint that will never answer, so
  // it stays off until there is something to talk to. Live updates come from
  // the polled /noc/summary in the meantime; `realtime` is otherwise ready.
  useEffect(() => {
    if (!import.meta.env['VITE_ENABLE_REALTIME']) return;
    realtime.connect();
    return () => realtime.disconnect();
  }, []);

  const toggleSidebar = () => {
    setCollapsed((value) => {
      localStorage.setItem('oss.sidebar', value ? 'expanded' : 'collapsed');
      return !value;
    });
  };

  return (
    <div className="flex h-full flex-col bg-base">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <TopBar onToggleSidebar={toggleSidebar} />

      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Modules"
          className="hidden shrink-0 border-r border-border bg-surface transition-[width] duration-200 ease-out md:block"
          style={{ width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)' }}
        >
          <ul className="flex flex-col gap-0.5 p-2">
            {/* Filtered by the same permission matrix the API guard uses, so a
                user is never shown a destination that will 403 on arrival. */}
            {NAV.filter((item) => has(item.permission)).map((item) =>
              item.built ? (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-accent-muted text-primary font-medium'
                          : 'text-secondary hover:bg-hover hover:text-primary',
                      ].join(' ')
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon size={18} className="shrink-0" aria-hidden />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </NavLink>
                </li>
              ) : (
                <li key={item.to}>
                  <span
                    aria-disabled="true"
                    title={`${item.label} — not built yet`}
                    className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2 text-sm text-muted opacity-55"
                  >
                    <item.icon size={18} className="shrink-0" aria-hidden />
                    {!collapsed && (
                      <>
                        <span className="truncate">{item.label}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-wide">soon</span>
                      </>
                    )}
                  </span>
                </li>
              ),
            )}
          </ul>
        </nav>

        {/* Keyed on pathname so navigating away from a broken page clears the
            error — without the key, a boundary that has caught stays caught
            and every subsequent route renders the failure message instead. */}
        <main id="main" className="min-w-0 flex-1 overflow-y-auto">
          <ErrorBoundary key={location.pathname} area="This page">
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

function TopBar({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') ?? 'dark',
  );

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('oss.theme', next);
    setTheme(next);
    // The server also stores the preference so it follows the user across
    // devices; failure to persist must not undo the visible change.
  };

  return (
    <header
      className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4"
      style={{ height: 'var(--topbar-h)' }}
    >
      <button
        onClick={onToggleSidebar}
        className="rounded-md p-1.5 text-secondary hover:bg-hover"
        aria-label="Toggle navigation"
      >
        <Gauge size={18} aria-hidden />
      </button>

      <span className="font-semibold tracking-tight">KIOBYTES OSS</span>

      <button
        className="ml-4 flex flex-1 items-center gap-2 rounded-md border border-border bg-inset px-3 py-1.5 text-sm text-muted hover:border-border-strong md:max-w-md"
        aria-label="Open command palette"
      >
        <Search size={15} aria-hidden />
        <span>Search…</span>
        <kbd className="ml-auto rounded border border-border px-1.5 py-0.5 text-xs">⌘K</kbd>
      </button>

      {/* Always visible, on every page. An engineer three levels deep in asset
          management still sees a P1 land — this is the single most important
          layout decision in the product. */}
      {/* Its own boundary. These counters are the one thing on screen at all
          times, and a fault in them must not be able to remove the navigation
          from every other page. */}
      <ErrorBoundary area="Alarm counters">
        <AlarmCounters />
      </ErrorBoundary>

      <AccountMenu />

      <button
        onClick={toggleTheme}
        className="rounded-md p-1.5 text-secondary hover:bg-hover"
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      >
        {theme === 'dark' ? <Sun size={18} aria-hidden /> : <Moon size={18} aria-hidden />}
      </button>
    </header>
  );
}

/**
 * Account menu.
 *
 * Exists because two-factor authentication is opt-in. Enrolment used to be
 * reachable only by being *forced* there — the guard redirected mandatory
 * accounts to `/settings/mfa` and nothing else linked to it. Making MFA
 * optional without adding this would have made it impossible to turn on, which
 * is not "optional", it is "removed".
 */
function AccountMenu() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-secondary hover:bg-hover"
      >
        <UserRound size={18} aria-hidden />
        <span className="hidden max-w-32 truncate sm:inline">{user?.fullName ?? 'Account'}</span>
        {/* A quiet nudge, not a nag. Shown once in the corner rather than as a
            banner on every page — the decision is the operator's. */}
        {user && !user.mfaEnabled && (
          <ShieldAlert
            size={14}
            aria-label="Two-factor authentication is not enabled"
            style={{ color: 'var(--sev-minor)' }}
          />
        )}
      </button>

      {open && (
        <>
          {/* Click-away layer. Without it the menu can only be closed by the
              button, which is a trap on touch. */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-border bg-surface p-1 shadow-2"
          >
            <div className="px-3 py-2">
              <p className="truncate text-sm font-medium">{user?.fullName}</p>
              <p data-identifier className="truncate text-xs text-muted">
                {user?.email}
              </p>
            </div>

            <NavLink
              to="/settings/mfa"
              onClick={() => setOpen(false)}
              className="block rounded px-3 py-2 text-sm text-secondary hover:bg-hover"
              role="menuitem"
            >
              Two-factor authentication
              <span className="ml-2 text-xs" style={{ color: user?.mfaEnabled ? 'var(--sev-ok)' : 'var(--text-muted)' }}>
                {user?.mfaEnabled ? 'on' : 'off'}
              </span>
            </NavLink>

            <button
              onClick={() => {
                tokenStore.clear();
                window.location.href = '/login';
              }}
              className="block w-full rounded px-3 py-2 text-left text-sm text-secondary hover:bg-hover"
              role="menuitem"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AlarmCounters() {
  // Reads the same query as the dashboard, so this costs no extra requests —
  // TanStack dedupes on the key. Previously it waited on a WebSocket that
  // never connects, which left the counters permanently blank: the one piece
  // of the UI that must be right on every page showed nothing at all.
  const { data } = useQuery({
    queryKey: ['noc', 'summary'],
    queryFn: () => api.get<NocSummary>('/noc/summary'),
    refetchInterval: 30_000,
    staleTime: 5_000,
  });

  const counts = data?.alarms.bySeverity ?? {};
  const p1 = counts['P1'] ?? 0;
  const p2 = counts['P2'] ?? 0;

  return (
    <div className="flex items-center gap-3" aria-live="polite">
      {/* Link, not <a>. An anchor here did a full page reload — losing the
          query cache and re-downloading the bundle at the exact moment a P1
          landed. The query string is what the alarm console reads. */}
      {p1 > 0 && (
        <NavLink
          to="/alarms?severity=P1"
          className="flex items-center gap-1.5 text-sm font-semibold"
          style={{ color: 'var(--sev-critical)' }}
        >
          <span aria-hidden className="sev-pulse">
            ⬤
          </span>
          <span data-metric>{p1}</span>
          <span className="sr-only">critical alarms</span>
        </NavLink>
      )}
      {p2 > 0 && (
        <NavLink
          to="/alarms?severity=P2"
          className="flex items-center gap-1.5 text-sm font-semibold"
          style={{ color: 'var(--sev-major)' }}
        >
          <span aria-hidden>◑</span>
          <span data-metric>{p2}</span>
          <span className="sr-only">major alarms</span>
        </NavLink>
      )}
    </div>
  );
}
